import React from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MapPin } from "lucide-react";
import Reveal from "../shared/Reveal";
import { Button } from "@/components/ui/button";

const SERVICE_LABELS = {
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  basement: "Basement",
  "whole-house": "Whole-House",
  exterior: "Exterior",
  other: "Other",
};

export default function FeaturedProjects() {
  const { data: projects = [] } = useQuery({
    queryKey: ["featured-projects"],
    queryFn: () => base44.entities.PortfolioProjects.list("display_order", 12),
    initialData: [],
  });

  // Only show projects that have a photo. Skip empty placeholder rows.
  const withImages = projects
    .filter((p) => p.after_image || (p.gallery_images && p.gallery_images.length > 0) || p.before_image)
    .slice(0, 6);
  if (!withImages.length) return null;

  return (
    <section className="py-20 lg:py-28 bg-secondary/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">Our Work</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Recent Projects
            </h2>
          </div>
          <Link to="/portfolio">
            <Button variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-white font-body font-semibold transition-colors">
              View Full Gallery <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {withImages.map((project, i) => {
            const img = project.after_image || (project.gallery_images && project.gallery_images[0]) || project.before_image;
            return (
              <Reveal
                key={project.id}
                delay={i * 70}
              >
                <Link to="/portfolio" className="group block bg-card rounded-xl overflow-hidden border border-border hover:shadow-xl transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden bg-muted">
                    {img ? (
                      <img loading="lazy" decoding="async"
                        src={img}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm font-body">
                        Upload hero image
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-body font-semibold text-accent uppercase tracking-wide">
                        {SERVICE_LABELS[project.service_type] || project.service_type}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground mb-1">{project.title}</h3>
                    {project.location && (
                      <p className="flex items-center gap-1 font-body text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" /> {project.location}
                      </p>
                    )}
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}